import React from 'react';

/**
 * CgTableSkeleton is a loading placeholder for CgTable that provides
 * a smooth, professional skeleton UI while data is being fetched.
 *
 * Mirrors the real table's geometry: pass `selectable` / `showActions`
 * so the leading checkbox column and trailing actions column reserve the
 * same width they do once rows render. Palette is slate to match the
 * table itself.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {number} props.rows - Number of skeleton rows to display (default: 5).
 * @param {number} props.columns - Number of skeleton columns to display (default: 5).
 * @param {boolean} props.showHeader - Whether to show the header skeleton (default: true).
 * @param {boolean} props.showPagination - Whether to show pagination skeleton (default: true).
 * @param {boolean} props.selectable - Reserve the leading checkbox column (default: false).
 * @param {boolean} props.showActions - Reserve the trailing actions column (default: false).
 * @returns {JSX.Element} The rendered skeleton component.
 */
const CgTableSkeleton = ({
  rows = 5,
  columns = 5,
  showHeader = true,
  showPagination = true,
  selectable = false,
  showActions = false,
}) => {
  // Generate varying widths for more natural look
  const getRandomWidth = (seed) => {
    const widths = ['w-16', 'w-20', 'w-24', 'w-28', 'w-32', 'w-36', 'w-40'];
    return widths[seed % widths.length];
  };

  const SkeletonCell = ({ seed = 0 }) => (
    <div className={`h-4 bg-slate-200 rounded animate-pulse ${getRandomWidth(seed)}`} />
  );

  const SkeletonHeaderCell = ({ seed = 0 }) => (
    <div className={`h-3 bg-slate-300 rounded animate-pulse ${getRandomWidth(seed + 2)}`} />
  );

  const SkeletonCheckbox = () => <div className="h-4 w-4 bg-slate-200 rounded animate-pulse" />;

  return (
    <div
      className="animate-fadeIn"
      aria-hidden="true"
    >
      {/* Table skeleton */}
      <div className="overflow-hidden">
        <table className="w-full divide-y divide-slate-100">
          {showHeader && (
            <thead>
              <tr>
                {selectable && (
                  <th
                    className="px-3 py-3"
                    style={{ width: '40px' }}
                  >
                    <SkeletonCheckbox />
                  </th>
                )}
                {Array.from({ length: columns }).map((_, idx) => (
                  <th
                    key={`skeleton-header-${idx}`}
                    className="px-3 py-3"
                  >
                    <SkeletonHeaderCell seed={idx} />
                  </th>
                ))}
                {showActions && <th className="px-3 py-3 w-10" />}
              </tr>
            </thead>
          )}
          <tbody className="bg-white divide-y divide-slate-100">
            {Array.from({ length: rows }).map((_, rowIdx) => (
              <tr
                key={`skeleton-row-${rowIdx}`}
                className="animate-pulse"
                style={{
                  animationDelay: `${rowIdx * 50}ms`,
                }}
              >
                {selectable && (
                  <td
                    className="px-3 py-4"
                    style={{ width: '40px' }}
                  >
                    <SkeletonCheckbox />
                  </td>
                )}
                {Array.from({ length: columns }).map((_, colIdx) => (
                  <td
                    key={`skeleton-cell-${rowIdx}-${colIdx}`}
                    className="px-3 py-4"
                  >
                    <SkeletonCell seed={rowIdx + colIdx} />
                  </td>
                ))}
                {showActions && <td className="px-3 py-4 w-10" />}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination skeleton */}
      {showPagination && (
        <>
          <hr
            style={{ borderTop: '2px solid #e2e8f0', marginTop: '8px' }}
            className="w-full"
          />
          <div className="pt-10 flex justify-between items-center">
            {/* Total results skeleton */}
            <div className="flex items-center space-x-2">
              <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
            </div>

            {/* Pagination buttons skeleton */}
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 bg-slate-200 rounded animate-pulse" />
              <div className="h-8 w-8 bg-slate-200 rounded animate-pulse" />
              <div className="h-8 w-8 bg-slate-200 rounded animate-pulse" />
              <div className="h-8 w-8 bg-slate-200 rounded animate-pulse" />
              <div className="h-8 w-8 bg-slate-200 rounded animate-pulse" />
            </div>

            {/* Page size selector skeleton */}
            <div className="h-8 w-20 bg-slate-200 rounded animate-pulse" />
          </div>
        </>
      )}
    </div>
  );
};

export default CgTableSkeleton;
